// Shared ECharts theme — ported from the TB-EMS SPA.
//
// All colour-bearing fields read from CSS custom properties at render time
// (`--ems-axis`, `--ems-grid`, `--ems-ink-*`, the SCADA domain vars, …) via
// `readVar`, so the theme adapts automatically when [data-theme="dark"]
// flips — no `isDark()` branching needed.
//
// Back-compat shims (`baseOption`, `chartColors`, `chartTokens`) keep the
// existing 8 chart components + 4 page chart-blocks compiling unchanged;
// the restyle sweep migrates them onto the richer helpers below.

import type { EChartsOption } from "echarts";

// ---------------------------------------------------------------------------
// CSS variable readers
// ---------------------------------------------------------------------------

// SSR/no-window fallbacks — kept in sync with the LIGHT (:root) token values
// in styles.css (smart-e Electric palette). The SPA is client-only, so these
// are rarely hit, but stay authoritative for any pre-paint/headless render.
const FALLBACKS: Record<string, string> = {
  "--ems-axis": "#4a5878",
  "--ems-grid": "#e1e8f1",
  "--ems-brand": "#1863dc",
  "--ems-rule-strong": "rgba(15,28,56,0.10)",
  "--ems-ink": "#15203a",
  "--ems-ink-soft": "rgba(21,32,58,0.62)",
  "--ems-ink-muted": "rgba(21,32,58,0.45)",
  "--ems-surface-raised": "#ffffff",
  "--ems-border": "#e1e8f1",
  "--ems-border-soft": "rgba(225,232,241,0.7)",
  "--ems-shadow-2": "0 4px 12px rgba(15,28,56,0.06)",
  "--ems-net": "#1e2940",
  "--ems-ok": "#16a34a",
  "--ems-warn": "#b45309",
  "--ems-stale": "#8d9bb3",
  "--ems-alarm-critical": "#dc2626",
  "--ems-charge": "#0c8ec9",
  "--ems-discharge": "#e0701c",
  "--ems-solar": "#e07f12",
  "--ems-grid-import": "#7059e6",
  "--ems-grid-export": "#0d9488",
  "--ems-price": "#0e8aa8",
  "--ems-price-cheap": "#0e7490",
  "--ems-price-dear": "#be185d",
  "--ems-font-numeric": "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
};

export function readVar(name: string): string {
  if (typeof window === "undefined") return FALLBACKS[name] ?? "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw.length > 0 ? raw : (FALLBACKS[name] ?? "");
}

/** Convenience: the chart-axis colour (text + tick). */
export const chartAxisColor = () => readVar("--ems-axis");
/** Convenience: the chart-grid line colour. */
export const chartGridColor = () => readVar("--ems-grid");
/** Convenience: the brand accent colour. */
export const chartBrandColor = () => readVar("--ems-brand");

// ---------------------------------------------------------------------------
// Token map — preferred entry point for new chart code.
// ---------------------------------------------------------------------------

export const themeColors = {
  axis: () => readVar("--ems-axis"),
  grid: () => readVar("--ems-grid"),
  ruleStrong: () => readVar("--ems-rule-strong"),
  ink: () => readVar("--ems-ink"),
  inkSoft: () => readVar("--ems-ink-soft"),
  inkMuted: () => readVar("--ems-ink-muted"),
  surface: () => readVar("--ems-surface-raised"),
  border: () => readVar("--ems-border-soft"),
  shadow: () => readVar("--ems-shadow-2"),
  numericFont: () => readVar("--ems-font-numeric"),
  brand: () => readVar("--ems-brand"),
  // Grid DIRECTION colours — one canonical pair app-wide (DSGN-05): the
  // SCADA violet/green. (The TB-EMS energy-METERING --ems-import/--ems-export
  // greens/ambers are intentionally no longer exposed here; direction words
  // always wear direction colours.)
  import: () => readVar("--ems-grid-import"),
  export: () => readVar("--ems-grid-export"),
  /** Net building load — its own neutral token, never a direction colour. */
  net: () => readVar("--ems-net"),
  // Operator-SPA additions — status + SCADA domain colours.
  ok: () => readVar("--ems-ok"),
  warn: () => readVar("--ems-warn"),
  critical: () => readVar("--ems-alarm-critical"),
  charge: () => readVar("--ems-charge"),
  discharge: () => readVar("--ems-discharge"),
  solar: () => readVar("--ems-solar"),
  price: () => readVar("--ems-price"),
  // Price VERDICT pair (G21) — distinct from the status ok/warn pair so a
  // "cheap/expensive" judgement never reads as "healthy/warning" power state.
  priceCheap: () => readVar("--ems-price-cheap"),
  priceDear: () => readVar("--ems-price-dear"),
  gridImport: () => readVar("--ems-grid-import"),
  gridExport: () => readVar("--ems-grid-export"),
} as const;

// ---------------------------------------------------------------------------
// Per-unit identity palette
// ---------------------------------------------------------------------------

/** Fixed cycle order for unit-identity hues — existing tokens only. */
const UNIT_COLOR_VARS = [
  "--ems-grid-import",
  "--ems-solar",
  "--ems-price",
  "--ems-grid-export",
  "--ems-discharge",
  "--ems-net",
  "--ems-brand",
  "--ems-charge",
] as const;

/**
 * Stable per-unit series colour: BESS-1 must wear the same hue on EVERY
 * chart, so all per-unit series colour by unit index through this one fixed
 * 8-hue cycle. Severity tokens (warn/critical) are deliberately excluded —
 * a degraded-state colour must never paint a healthy data series. Reads the
 * CSS var live at call time, so call INSIDE chart option memos (keyed on
 * useThemeVersion) and theme flips re-resolve for free.
 */
export function unitColor(idx: number): string {
  return readVar(UNIT_COLOR_VARS[Math.abs(idx) % UNIT_COLOR_VARS.length]);
}

/** hex → rgba string. Shared by every chart (replaces the per-file copies). */
export function hexA(hex: string, a: number): string {
  if (!hex.startsWith("#")) return hex;
  const h = hex.slice(1);
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// ---------------------------------------------------------------------------
// Base option fragments — spread into chart options, override per chart
// ---------------------------------------------------------------------------

/** Default grid padding. */
export function gridBase(opts?: { hasRightAxis?: boolean }) {
  return {
    left: 64,
    right: opts?.hasRightAxis ? 64 : 16,
    top: 24,
    bottom: 36,
    containLabel: false,
  };
}

/** Tooltip — card surface, mono numerics, adapts via CSS vars. */
export function tooltipBase() {
  return {
    trigger: "axis" as const,
    backgroundColor: themeColors.surface(),
    borderColor: themeColors.border(),
    borderWidth: 1,
    padding: [8, 12, 8, 12],
    extraCssText: `box-shadow: ${themeColors.shadow()}; border-radius: 10px; backdrop-filter: blur(4px);`,
    textStyle: {
      color: themeColors.ink(),
      fontFamily: themeColors.numericFont(),
      fontSize: 12,
    },
    axisPointer: {
      type: "cross" as const,
      lineStyle: { color: themeColors.ruleStrong(), width: 1, type: "dashed" as const },
      crossStyle: { color: themeColors.ruleStrong(), width: 1 },
      label: {
        backgroundColor: themeColors.inkSoft(),
        color: themeColors.surface(),
        fontFamily: themeColors.numericFont(),
        fontSize: 11,
        padding: [3, 6, 3, 6],
        borderRadius: 4,
      },
    },
  };
}

/** X-axis (time-series). Labels in mono, no axis line, no ticks. */
export function xAxisTime() {
  return {
    type: "time" as const,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: themeColors.axis(),
      fontFamily: themeColors.numericFont(),
      fontSize: 11,
      margin: 12,
      hideOverlap: true,
    },
    splitLine: { show: false },
    boundaryGap: false,
  };
}

/** X-axis (category — period bars). Labels in mono. */
export function xAxisCategory(categories: string[]) {
  return {
    type: "category" as const,
    data: categories,
    axisLine: { lineStyle: { color: themeColors.grid(), width: 1 } },
    axisTick: { show: false },
    axisLabel: {
      color: themeColors.axis(),
      fontFamily: themeColors.numericFont(),
      fontSize: 11,
      margin: 10,
      hideOverlap: true,
    },
    splitLine: { show: false },
  };
}

/** Y-axis (value). Dashed low-contrast grid so it never competes with data. */
export function yAxisValue(opts?: {
  position?: "left" | "right";
  formatter?: (v: number) => string;
  name?: string;
  showSplitLine?: boolean;
}) {
  return {
    type: "value" as const,
    position: opts?.position ?? ("left" as const),
    name: opts?.name,
    nameLocation: "end" as const,
    nameGap: 12,
    nameTextStyle: {
      color: themeColors.inkMuted(),
      fontFamily: themeColors.numericFont(),
      fontSize: 10,
      align: opts?.position === "right" ? ("right" as const) : ("left" as const),
    },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: themeColors.axis(),
      fontFamily: themeColors.numericFont(),
      fontSize: 11,
      formatter: opts?.formatter,
      margin: 10,
    },
    splitLine: {
      show: opts?.showSplitLine !== false,
      lineStyle: {
        color: themeColors.grid(),
        type: [4, 4] as [number, number],
        width: 1,
        opacity: 0.65,
      },
    },
  };
}

/** Subtle 1px horizontal rule at y = 0. Use as a series `markLine`. */
export function zeroLineMarker() {
  return {
    silent: true,
    symbol: "none" as const,
    lineStyle: { color: themeColors.ruleStrong(), width: 1, type: "solid" as const },
    label: { show: false },
    data: [{ yAxis: 0 }],
  };
}

/** Animation defaults — single fluent reveal. */
export function animationBase() {
  return {
    animation: true,
    animationDuration: 360,
    animationEasing: "cubicOut" as const,
  };
}

/** Bar style — top-rounded for positive, bottom-rounded for negative. */
export function barItemStyle(opts: { sign: "positive" | "negative"; color: string }) {
  const radius =
    opts.sign === "positive"
      ? ([3, 3, 0, 0] as [number, number, number, number])
      : ([0, 0, 3, 3] as [number, number, number, number]);
  return { color: opts.color, borderRadius: radius };
}

/** Line style with a subtle area gradient. */
export function lineSeriesStyle(opts: { color: string; negative?: boolean }) {
  return {
    smooth: 0.2 as const,
    symbol: "none" as const,
    itemStyle: { color: opts.color },
    lineStyle: { color: opts.color, width: 2.25, cap: "round" as const },
    areaStyle: {
      opacity: 0.16,
      color: {
        type: "linear" as const,
        x: 0,
        y: opts.negative ? 1 : 0,
        x2: 0,
        y2: opts.negative ? 0 : 1,
        colorStops: [
          { offset: 0, color: opts.color },
          { offset: 1, color: `${opts.color}00` },
        ],
        global: false,
      },
    },
  };
}

/** Shared dataZoom slider styling — replaces the per-chart copies. */
export function dataZoomSlider(opts?: { color?: string }) {
  const accent = opts?.color ?? themeColors.brand();
  const grid = themeColors.grid();
  // hexA needs a hex literal — when the resolved accent isn't hex (e.g. a
  // color-mix() result) fall back to the token's canonical fallback value.
  const accentHex = accent.startsWith("#") ? accent : FALLBACKS["--ems-brand"];
  return {
    type: "slider" as const,
    xAxisIndex: 0,
    height: 20,
    bottom: 4,
    borderColor: grid,
    fillerColor: hexA(accentHex, 0.08),
    handleStyle: { color: accent, borderColor: accent },
    moveHandleStyle: { color: accent },
    textStyle: { fontSize: 10, color: themeColors.axis() },
    dataBackground: {
      lineStyle: { color: grid },
      areaStyle: { color: hexA(accentHex, 0.05) },
    },
  };
}

// ---------------------------------------------------------------------------
// Tooltip HTML formatter — card-style with mono numerics
// ---------------------------------------------------------------------------

export type TooltipRow = {
  color: string;
  label: string;
  value: string;
  unit?: string;
  hint?: string;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Build the tooltip HTML the way every chart in the SPA renders it. */
export function tooltipHtml(args: { header: string; rows: TooltipRow[] }): string {
  const numFont = themeColors.numericFont();
  const headerEl = `<div style="font-family: ${numFont};
       font-size: 11px;
       color: var(--ems-ink-muted);
       padding-bottom: 6px;
       border-bottom: 1px solid var(--ems-rule-strong);
       margin-bottom: 6px;
       letter-spacing: 0.02em;
       white-space: nowrap;">${escapeHtml(args.header)}</div>`;

  const rowsEl = args.rows
    .map((r) => {
      const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:9999px;background:${r.color};margin-right:8px;flex-shrink:0;"></span>`;
      const label = `<span style="color: var(--ems-ink-soft); font-size: 11px;">${escapeHtml(r.label)}</span>`;
      const value = `<span style="font-family: ${numFont};
           font-feature-settings: 'tnum' 1;
           font-weight: 600;
           color: var(--ems-ink);
           font-size: 12px;
           letter-spacing: -0.01em;">${escapeHtml(r.value)}${r.unit ? `<span style="color: var(--ems-ink-muted); font-weight: 400; margin-left: 3px;">${escapeHtml(r.unit)}</span>` : ""}</span>`;
      const hint = r.hint
        ? `<div style="margin-left: 16px; color: var(--ems-ink-muted); font-size: 10px; margin-top: 1px;">${escapeHtml(r.hint)}</div>`
        : "";
      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;padding:2px 0;">
        <div style="display:flex;align-items:center;min-width:0;">${dot}${label}</div>
        ${value}
      </div>${hint}`;
    })
    .join("");

  return `<div style="min-width: 180px;">${headerEl}${rowsEl}</div>`;
}

// ---------------------------------------------------------------------------
// Back-compat shims — keep the existing chart components compiling unchanged.
// `chartColors` is a module-load snapshot; `chartTokens()` re-reads live.
// ---------------------------------------------------------------------------

function buildTokens() {
  return {
    text: readVar("--ems-ink"),
    textMuted: readVar("--ems-ink-soft"),
    textFaint: readVar("--ems-ink-muted"),
    axis: readVar("--ems-axis"),
    grid: readVar("--ems-grid"),
    surface: readVar("--ems-surface-raised"),
    edge: readVar("--ems-border"),
    tooltipBg: readVar("--ems-surface-raised"),
    tooltipBorder: readVar("--ems-border"),
    accent: readVar("--ems-brand"),
    brand: readVar("--ems-brand"),
    ok: readVar("--ems-ok"),
    warn: readVar("--ems-warn"),
    critical: readVar("--ems-alarm-critical"),
    charge: readVar("--ems-charge"),
    discharge: readVar("--ems-discharge"),
    solar: readVar("--ems-solar"),
    import: readVar("--ems-grid-import"),
    export: readVar("--ems-grid-export"),
    /** Net building load — neutral, never a direction colour (DSGN-05). */
    net: readVar("--ems-net"),
    price: readVar("--ems-price"),
  };
}

/** Live token read — call inside chart render/memo functions. */
export function chartTokens() {
  return buildTokens();
}

/** Module-load snapshot — older code references the object directly. Prefer
 *  `chartTokens()` inside render functions for live-correct (theme-aware)
 *  colours. */
export const chartColors = buildTokens();

export function baseOption(): EChartsOption {
  const t = buildTokens();
  return {
    backgroundColor: "transparent",
    textStyle: { fontFamily: "Roboto, system-ui, sans-serif", color: t.text },
    grid: { top: 28, left: 52, right: 16, bottom: 28, containLabel: true },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: t.axis } },
      axisTick: { lineStyle: { color: t.axis } },
      axisLabel: { color: t.textMuted, fontSize: 11 },
      splitLine: { show: false },
    } as object,
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: t.textMuted, fontSize: 11 },
      splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
    } as object,
    legend: {
      top: 4,
      right: 8,
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      textStyle: { color: t.textMuted, fontSize: 11 },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: t.tooltipBg,
      borderColor: t.tooltipBorder,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: t.text, fontFamily: "Roboto, system-ui, sans-serif", fontSize: 11 },
      axisPointer: {
        type: "cross",
        lineStyle: { color: t.textFaint, type: "dashed" },
        crossStyle: { color: t.textFaint, type: "dashed" },
        label: { backgroundColor: t.tooltipBorder, color: t.text },
      },
    },
  };
}
